import moment from 'moment';
import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';
import { useAppSelector } from '../../../contexts/hooks';
import { useScreenSize } from '../../../utils/media-query';
import '../home.scss';

const AVATAR_BG = '#fff';
const AVATAR_COLOR = '#1976d2';
const INFO_LABEL_WIDTH = 130;

export const BoardPersonalInfo = () => {
  const institutionName = useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'IntitutionName')?.Cgn_Resultado);
  const userInfo = useAppSelector((x) => x.userInfo.User);
  const localtion = useAppSelector((x) => x.userInfo.Location);
  const perfil = useAppSelector((x) => x.userInfo.Profile);
  const systemLocations = useAppSelector((data) => data.configuration.Data?.ComboBox)?.find((x) => x.Name === 'SysI_Sedes')?.Data;
  const { isXSmall, isSmall } = useScreenSize();
  // Truncamiento y tooltip para nombre largo
  const getUserFullName = () => {
    const fullName = `${userInfo?.Us_Nombre || ''} ${userInfo?.Us_Apellido1 || ''} ${userInfo?.Us_Apellido2 || ''}`.trim() || 'No disponible';
    const maxLength = 26;
    if (fullName.length > maxLength) {
      return (
        <span title={fullName} style={{ cursor: 'help' }}>
          {fullName.substring(0, maxLength)}...
        </span>
      );
    }
    return fullName;
  };
  // Badge premium mejorado
  const Badge = ({ text, color = '#fff', bg = '#1976d2' }) => (
    <span
      className="profile-badge"
      style={{
        background: `linear-gradient(135deg, ${bg} 0%, #1565c0 100%)`,
        color,
        borderRadius: 16,
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: '0.8rem',
        marginLeft: isXSmall || isSmall ? 0 : 8,
        marginTop: isXSmall || isSmall ? 4 : 0,
        letterSpacing: 0.3,
        boxShadow: '0 2px 8px rgba(25,118,210,0.2)',
        whiteSpace: 'nowrap',
        cursor: 'default',
      }}>
      {text}
    </span>
  );

  // Componente InfoRow para consistencia
  const InfoRow = ({ label, value, showInMobile = true }: { label: string; value: string; showInMobile?: boolean }) => {
    if (!showInMobile && (isXSmall || isSmall)) return null;
    
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 8,
          justifyContent: isXSmall || isSmall ? 'center' : 'flex-start',
          flexDirection: isXSmall || isSmall ? 'column' : 'row',
          gap: isXSmall || isSmall ? 4 : 0,
        }}>
        <span
          className="info-label"
          style={{
            fontWeight: 600,
            color: '#374151',
            minWidth: INFO_LABEL_WIDTH,
            textAlign: isXSmall || isSmall ? 'center' : 'right',
            marginRight: isXSmall || isSmall ? 0 : 12,
            fontSize: '0.9rem',
          }}>
          {label}:
        </span>
        <span
          style={{
            fontWeight: 500,
            color: '#1976d2',
            fontSize: '0.95rem',
            textAlign: isXSmall || isSmall ? 'center' : 'left',
          }}>
          {value}
        </span>
      </div>
    );
  };  return (
    <CardAnalytics title='Información General' contentClass='personal-info'>
      <div
        style={{
          display: 'flex',
          flexDirection: isXSmall || isSmall ? 'column' : 'row',
          alignItems: isXSmall || isSmall ? 'center' : 'flex-start',
          gap: isXSmall || isSmall ? 16 : 20,
          padding: isXSmall || isSmall ? '1rem 0.5rem' : '1.5rem 1rem',
          minHeight: 180,
        }}>        {/* Avatar mejorado */}
        <div
          className="avatar-container"
          style={{
            width: 70,
            height: 70,
            minWidth: 70,
            minHeight: 70,
            borderRadius: '50%',
            background: AVATAR_BG,
            color: AVATAR_COLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            fontWeight: 400,
            boxShadow: '0 4px 12px rgba(25,118,210,0.15)',
            border: '2px solid #1976d2',
            marginBottom: isXSmall || isSmall ? 8 : 0,
            cursor: 'default',
          }}>
          👤
        </div>

        {/* Información principal */}
        <div style={{ flex: 1, width: '100%' }}>
          {/* Nombre y perfil */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 12,
              justifyContent: isXSmall || isSmall ? 'center' : 'flex-start',
              flexDirection: isXSmall || isSmall ? 'column' : 'row',
              gap: isXSmall || isSmall ? 6 : 0,
            }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: '1.15rem',
                color: '#1976d2',
                textAlign: isXSmall || isSmall ? 'center' : 'left',
                lineHeight: 1.3,
              }}>
              {isXSmall || isSmall 
                ? `${userInfo?.Us_Nombre || ''} ${userInfo?.Us_Apellido1 || ''} ${userInfo?.Us_Apellido2 || ''}`.trim() || 'No disponible' 
                : getUserFullName()
              }
            </span>
            {perfil?.Pe_Nombre && (
              <Badge text={perfil.Pe_Nombre} />
            )}
          </div>

          {/* Información detallada */}
          <div style={{ marginTop: 4 }}>
            <InfoRow 
              label="Sede" 
              value={systemLocations?.find((x) => x.Sd_Id === localtion)?.Sd_Nombre || 'No disponible'} 
            />
            
            <InfoRow 
              label="Última conexión" 
              value={userInfo?.Us_UltimoIngreso 
                ? moment(userInfo.Us_UltimoIngreso).format('DD/MM/YYYY hh:mm A') 
                : moment().format('DD/MM/YYYY hh:mm A')
              } 
            />
            
            <InfoRow 
              label="Base de datos" 
              value="No disponible" 
            />
          </div>          {/* Separador y nombre del laboratorio */}
          <div className="lab-section">
            <InfoRow 
              label="Laboratorio" 
              value={institutionName || 'No disponible'} 
            />
          </div>
        </div>
      </div>
    </CardAnalytics>
  );
};
